import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Loader2, Lock, Save, AlertTriangle } from 'lucide-react';
import { ReportSelectionButton } from '../components/savedSelections/ReportSelectionButton';
import { useAuth } from '../contexts/AuthContext';
import { SelectionRows } from '../components/savedSelections/SelectionCard';
import { AmountsEditor, amountsFrom, amountsPayload } from '../components/savedSelections/AmountsEditor';
import { SEL_API } from '../lib/selectionShare';
import { SaveNotifyAsk } from '../components/savedSelections/SaveNotifyAsk';
import { kindLabel } from '../components/savedSelections/selectionKind';
import { rememberInvite, playUrlWithInvite } from '../lib/inviteCode';

const PLAY_URL = 'https://play.google.com/store/apps/details?id=it.rvincente.app.twa';
const APP_STORE_URL = 'https://apps.apple.com/app/rvincente-pronostici-calcio/id6760314785';
export const PENDING_SHARE_KEY = 'rv_pending_share';

const mobileStore = () => {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return PLAY_URL;
  if (/iphone|ipad|ipod/i.test(ua)) return APP_STORE_URL;
  return null;
};

// whoever shared the selection invites whoever signs up from the link (same rules as the friend code)
const fetchShared = (code) => axios.get(`${SEL_API}/shared/${code}`).then((r) => r.data);

// Shared selection link (/s/:code): app installed → opens here; on a phone without the app → store
export default function SharedSelectionPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const store = useMemo(() => (Capacitor.isNativePlatform() ? null : mobileStore()), []);
  const [data, setData] = useState(null);
  const [amounts, setAmounts] = useState(null);
  const [saving, setSaving] = useState(false);
  const [askId, setAskId] = useState(null);

  useEffect(() => {
    if (store) {
      let t;
      const go = (inviter) => {
        t = setTimeout(() => { window.location.href = store === PLAY_URL && inviter ? playUrlWithInvite(inviter, 'selezione_condivisa') : store; }, 1200);
      };
      fetchShared(code).then((d) => { rememberInvite(d.inviter_code); go(d.inviter_code); }).catch(() => go(null));
      return () => clearTimeout(t);
    }
    if (authLoading) return undefined;
    if (!user) {
      localStorage.setItem(PENDING_SHARE_KEY, code);
      fetchShared(code).then((d) => rememberInvite(d.inviter_code)).catch(() => {})
        .finally(() => navigate('/login?tab=register', { replace: true }));
      return undefined;
    }
    localStorage.removeItem(PENDING_SHARE_KEY);
    axios.get(`${SEL_API}/shared/${code}`).then((r) => {
      setData(r.data);
      setAmounts(amountsFrom(r.data.stake, r.data.potential));
    }).catch(() => setData({ error: true }));
    return undefined;
  }, [code, store, user, authLoading, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${SEL_API}/shared/${code}/save`, amountsPayload(amounts));
      toast.success('Selezione salvata nelle tue selezioni');
      setAskId(data.selection_id);
    } catch {
      toast.error('Salvataggio non riuscito, riprova');
      setSaving(false);
    }
  };

  if (store) return <div className="min-h-screen bg-[#121212] flex items-center justify-center text-gray-300 text-sm" data-testid="shared-store-redirect">Ti porto allo store per scaricare RVINCENTE…</div>;
  if (!data) return <div className="min-h-screen bg-[#121212] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>;
  if (data.error) return <div className="min-h-screen bg-[#121212] flex items-center justify-center text-gray-400 text-sm p-6 text-center" data-testid="shared-not-found">Selezione non trovata o non più disponibile.</div>;

  return (
    <div className="min-h-screen bg-[#121212] px-4 pt-6 pb-28 max-w-md mx-auto" data-testid="shared-selection-page">
      {askId && <SaveNotifyAsk selectionId={askId} onDone={() => navigate('/diario?saved=1', { replace: true })} />}
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Selezione condivisa</p>
        <ReportSelectionButton code={code} />
      </div>
      <h1 className="text-white font-black text-xl mb-1" data-testid="shared-owner">Di {data.owner_name}</h1>
      {data.description ? <p className="text-[#F5A623] font-black text-sm mb-4" data-testid="shared-description">{data.description}</p> : <div className="mb-3" />}
      {data.locked ? (
        <button type="button" onClick={() => navigate('/vip?da=selezione')} className="w-full rounded-2xl border border-[#F5A623]/40 bg-[#1A1A1A] p-8 flex flex-col items-center gap-3" data-testid="shared-locked">
          <Lock className="w-8 h-8 text-[#F5A623]" />
          <p className="text-white font-bold">{data.match_count} partite</p>
          <p className="text-gray-400 text-sm">Le selezioni condivise sono riservate a VIP e GOLD. Tocca per sbloccarle.</p>
        </button>
      ) : (
        <>
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#1A1A1A] mb-4">
            <p className="px-3 py-2 text-[11px] font-black text-[#F5A623] bg-[#161616]" data-testid="shared-kind">{kindLabel(data)}</p>
            {data.booking_code ? <p className="px-3 py-2.5 text-[11px] text-gray-400 bg-[#161616]">Codice precompilata: <span className="text-white font-bold" data-testid="shared-code">{data.booking_code}</span></p> : null}
            <SelectionRows picks={data.picks} id={code} />
          </div>
          <p className="text-[11px] text-gray-500 mb-2">Sul sito dove giochi le quote possono essere diverse: modifica gli importi prima di salvare.</p>
          <AmountsEditor value={amounts} onChange={setAmounts} testid="shared-amounts" />
          <p className="mt-5 rounded-xl bg-[#F5A623]/10 border border-[#F5A623]/40 text-[#F5A623] text-xs p-3 flex gap-2" data-testid="shared-save-disclaimer">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Gli importi proposti sono quelli tracciati dall'autore della selezione. Modificali secondo la tua disponibilità prima di confermare. Confermando il salvataggio confermi di essere consapevole della tua azione autonoma. Gioca responsabilmente.</span>
          </p>
          <button type="button" onClick={save} disabled={saving} className="mt-3 w-full py-3 rounded-xl bg-[#F5A623] text-[#1A1A1A] font-black flex items-center justify-center gap-2" data-testid="shared-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva nel Diario
          </button>
        </>
      )}
    </div>
  );
}
